import React, { useEffect, useRef, useState } from "react";

const RESET_WINDOW_POSITION_EVENT = "FBX_RESET_WINDOW_POSITION";

/**
 * DragBox
 *
 * Props:
 * - storageKey: string (required)  // where we persist the position in localStorage
 * - defaultPos: { x:number, y:number } (required)
 * - title: string
 * - width: number (default 700)
 * - children: ReactNode
 *
 * Optional:
 * - onAddFunction: function | null
 * - onAddText: string (default '+')
 * - addNote: string | '-'  (send '-' to hide + button)
 *
 * - closeButton: boolean (default false)
 * - onCloseButton: function (called when Close clicked)
 *
 * NEW (Third Button):
 * - showThirdButton: boolean (default false)
 * - thirdButtonText: string (button label)
 * - thirdButtonClick: function (called when third button clicked)
 */
const DragBox = ({
  storageKey,
  defaultPos,
  title,
  width = 700,
  children,
  onAddFunction = null,
  onAddText = "+",
  addNote, // send this as '-' if you want to hide it on the drag box

  closeButton = false,
  onCloseButton = null,

  // ✅ third button
  showThirdButton = false,
  thirdButtonText = "Action",
  thirdButtonClick = null,
}) => {
  // ---- load & persist position ----
  const loadSavedPosition = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : defaultPos;
    } catch {
      return defaultPos;
    }
  };

  const [position, setPosition] = useState(loadSavedPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragOffsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const next = {
      x: e.clientX - dragOffsetRef.current.x,
      y: e.clientY - dragOffsetRef.current.y,
    };
    setPosition(next);
  };

  const onMouseUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    const finalPos = {
      x: e.clientX - dragOffsetRef.current.x,
      y: e.clientY - dragOffsetRef.current.y,
    };
    setPosition(finalPos);
    try {
      localStorage.setItem(storageKey, JSON.stringify(finalPos));
    } catch {}
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  useEffect(() => {
    const handleExternalReset = (event) => {
      const key = event?.detail?.storageKey || "";
      if (key !== storageKey && key !== "*") return;

      try {
        localStorage.removeItem(storageKey);
      } catch {}

      setPosition(defaultPos);
    };

    window.addEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
    return () => window.removeEventListener(RESET_WINDOW_POSITION_EVENT, handleExternalReset);
  }, [storageKey, defaultPos]);

  const resetPosition = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {}
    setPosition(defaultPos);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (typeof onCloseButton === "function") {
      onCloseButton();
    }
  };

  const handleThirdButton = (e) => {
    e.stopPropagation();
    if (typeof thirdButtonClick === "function") {
      thirdButtonClick();
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        zIndex: 500,
        minWidth: width,
        maxWidth: width,
        border: "2px solid navy",
        borderRadius: 8,
        backgroundColor: "var(--bs-body-bg)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
      className="card shadow-sm"
    >
      <div
        className="card-header d-flex align-items-center p-0"
        onMouseDown={onMouseDown}
        style={{ cursor: "grab", userSelect: "none" }}
        title="Drag"
      >
        <div
          className="d-flex align-items-center col-48 p-2"
          style={{ backgroundColor: "rgba(178, 182, 182, 0.1)" }}
        >
          <div style={{ marginLeft: "5px", pointerEvents: "none" }} tabIndex={-1}>
            ⠿
          </div>

          {/* Reset moved to LEFT side */}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            style={{ marginLeft: 8, height: 30, padding: "0 10px" }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              resetPosition();
            }}
            title="Reset position"
          >
            Reset Position
          </button>

          <div className="text-primary flex-grow-1 text-center fs-7">{title}</div>
          {/* ✅ Third button: only show if showThirdButton === true */}
          {showThirdButton === true && (
            <button
              type="button"
              className="btn btn-sm btn-outline-warning"
              style={{ marginRight: "45px", height: 30, padding: "0 10px" }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleThirdButton}
              aria-label="Third Button"
              title={thirdButtonText}
            >
              {thirdButtonText}
            </button>
          )}

          {/* + button should NOT start a drag; stop the mousedown */}
          {addNote !== "-" && (
            <button
              className="btn btn-sm btn-outline-success"
              style={{ marginRight: "5px", height: 30, padding: "0 8px" }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onAddFunction && onAddFunction();
              }}
              aria-label="Add"
              title={addNote}
            >
              {onAddText}
            </button>
          )}

          

          {/* Close button: only show if closeButton === true */}
          {closeButton === true && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ height: 30, padding: "0 10px" }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleClose}
              aria-label="Close"
              title="Close"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {children && <div className="card-body p-2">{children}</div>}
    </div>
  );
};

export default DragBox;
